/**
 * TODO: Have qualified legal counsel review and approve this Terms of Service
 * before relying on it for production launch or regulatory compliance.
 * This is a product-ready starter policy, not legal advice.
 */
import { LEGAL_LAST_UPDATED, SUPPORT_EMAIL } from './constants';
import type { LegalSection } from './types';

export const TERMS_PAGE_TITLE = 'Terms of Service';

export const TERMS_BETA_NOTICE =
  'Scoutlytics is currently in beta. These Terms of Service are subject to revision as our legal review progresses.';

export const TERMS_INTRO = [
  'These Terms of Service ("Terms") govern your access to and use of Scoutlytics, League Stream Center, and related websites, applications, and services (collectively, the "Service") operated by Scoutlytics ("we," "us," or "our").',
  `Last updated: ${LEGAL_LAST_UPDATED}.`,
  'By accessing or using the Service, you agree to these Terms. If you do not agree, do not use the Service.',
];

export const TERMS_SECTIONS: LegalSection[] = [
  {
    id: 'acceptance',
    title: '1. Acceptance of Terms',
    paragraphs: [
      'These Terms form a binding agreement between you and Scoutlytics. If you use the Service on behalf of a league, organization, or other entity, you represent that you have authority to bind that entity to these Terms.',
    ],
  },
  {
    id: 'service-description',
    title: '2. What Scoutlytics and League Stream Center Do',
    paragraphs: [
      'Scoutlytics provides tools that help users organize, review, and analyze sports-game-related video and league information. League Stream Center ("Stream Center" or "LSC") helps commissioners and league members import schedules, sync Twitch VOD metadata, match streams to competition records, review suggested matches, and publish verified archive entries for league viewing.',
      'Scoutlytics may analyze Twitch VOD metadata, frame evidence, league schedules, team assignments, usernames, gamer tags, Discord/Twitch identifiers, and related league content to organize streams, match VODs to games, and support commissioner review.',
      'Separate Scoutlytics features may provide AI-assisted video analysis and scouting reports. Those features are optional and subject to the limitations described in these Terms.',
    ],
  },
  {
    id: 'eligibility',
    title: '3. Eligibility and Account Responsibility',
    paragraphs: [
      'You must be able to form a binding contract in your jurisdiction to use the Service. You are responsible for maintaining the confidentiality of your account credentials and for all activity under your account.',
      'You agree to provide accurate information and to keep account details current where the Service allows updates.',
    ],
  },
  {
    id: 'third-party-integrations',
    title: '4. Twitch, Discord, and Third-Party Integrations',
    paragraphs: [
      'The Service may integrate with third-party platforms such as Twitch and Discord when you choose to connect them. Your use of those platforms remains subject to their own terms and policies.',
      'Scoutlytics is not affiliated with, endorsed by, or sponsored by Twitch, Discord, EA, Madden, NeonSportz, or other third-party platforms unless we explicitly state otherwise.',
      'We may change or discontinue integrations if a third party changes its APIs, policies, or availability.',
    ],
  },
  {
    id: 'user-content',
    title: '5. User-Submitted Content and League Data',
    paragraphs: [
      'The Service may process user-submitted content and league data, including schedules, standings, match records, usernames, gamer tags, league logos, imported CSV or NeonSportz data, Twitch VOD links, VOD metadata, commissioner review notes, and related league information ("User Content").',
      'You retain ownership of your User Content. Scoutlytics does not claim ownership of Twitch streams, league content, user gameplay footage, or third-party platform content.',
      'You are responsible for ensuring you have the rights or permission to submit, import, upload, link, or display User Content through the Service, including league logos, schedules, roster data, and VOD links.',
    ],
  },
  {
    id: 'license',
    title: '6. License to Process User Content',
    paragraphs: [
      'You grant Scoutlytics a non-exclusive, worldwide, royalty-free license to host, store, reproduce, process, display, and use User Content solely as needed to operate, secure, improve, and provide the Service, including league organization, VOD matching, commissioner review workflows, archive publication you authorize, customer support, and legal compliance.',
      'This license ends when User Content is deleted from the Service, except where retention is permitted by our Privacy Policy or required by law.',
    ],
  },
  {
    id: 'prohibited-uses',
    title: '7. Prohibited Uses',
    bullets: [
      'Using the Service in violation of applicable law or third-party rights.',
      'Attempting to access accounts, leagues, or data without authorization.',
      'Uploading malware, abusive content, or content you do not have rights to use.',
      'Reverse engineering, scraping, or interfering with Service security or availability except as permitted by law.',
      'Misrepresenting league authority, commissioner status, or ownership of streams or league assets.',
      'Using the Service to harass, dox, or exploit others.',
    ],
    paragraphs: [
      'You agree not to misuse the Service. Without limiting the foregoing, you will not:',
    ],
  },
  {
    id: 'commissioner-responsibilities',
    title: '8. Commissioner and Member Responsibilities',
    paragraphs: [
      'Commissioners and league administrators are responsible for league configuration, member access, review decisions, publication choices, and ensuring league data and published archive entries reflect appropriate permissions within their league.',
      'Members are responsible for using published league content appropriately and for maintaining accurate profile and identity information where applicable.',
    ],
  },
  {
    id: 'ai-limitations',
    title: '9. AI and Video Analysis Limitations',
    paragraphs: [
      'Some Service features use automated processing, including AI-assisted frame analysis, metadata matching, and suggested labels such as detected teams, gamer tags, or match candidates.',
      'AI, OCR, metadata matching, and frame verification can be wrong, incomplete, or inconclusive. Outputs are assistive only and should be reviewed by commissioners and users before relying on them for publication, competition decisions, or other important uses.',
      'Scoutlytics does not guarantee perfect accuracy, completeness, or fitness for any particular competitive, legal, or business purpose.',
    ],
  },
  {
    id: 'payments',
    title: '10. Payments and Subscriptions',
    paragraphs: [
      'Some features may require payment, credits, or subscriptions in the future or in certain environments. If paid features are offered, pricing, billing terms, renewal, cancellation, and refund rules will be presented at the point of purchase and may be updated from time to time.',
      'Unless required by law, fees already paid may be non-refundable except as stated in the applicable purchase terms.',
    ],
  },
  {
    id: 'availability',
    title: '11. Service Availability',
    paragraphs: [
      'We strive to keep the Service available, but we do not guarantee uninterrupted or error-free operation. Maintenance, third-party outages, API changes, and other events may cause delays or temporary unavailability.',
    ],
  },
  {
    id: 'intellectual-property',
    title: '12. Intellectual Property',
    paragraphs: [
      'The Service, including its software, branding, design, and documentation (excluding User Content), is owned by Scoutlytics or its licensors and is protected by intellectual property laws. No rights are granted except as expressly stated in these Terms.',
    ],
  },
  {
    id: 'third-party-services',
    title: '13. Third-Party Services and Links',
    paragraphs: [
      'The Service may link to or embed third-party services, including Twitch players, authentication providers, hosting providers, and data sources selected by users. We are not responsible for third-party services and do not control their content or policies.',
    ],
  },
  {
    id: 'termination',
    title: '14. Termination',
    paragraphs: [
      'You may stop using the Service at any time. We may suspend or terminate access if we reasonably believe you violated these Terms, created risk for the Service or other users, or as required by law.',
      'Sections that by their nature should survive termination will survive, including ownership, disclaimers, limitations of liability, and dispute-related terms.',
    ],
  },
  {
    id: 'disclaimers',
    title: '15. Disclaimers',
    paragraphs: [
      'THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE." TO THE MAXIMUM EXTENT PERMITTED BY LAW, SCOUTLYTICS DISCLAIMS ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.',
    ],
  },
  {
    id: 'liability',
    title: '16. Limitation of Liability',
    paragraphs: [
      'TO THE MAXIMUM EXTENT PERMITTED BY LAW, SCOUTLYTICS WILL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, EXEMPLARY, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS, DATA, GOODWILL, OR BUSINESS OPPORTUNITY, ARISING FROM OR RELATED TO THE SERVICE OR THESE TERMS.',
      'TO THE MAXIMUM EXTENT PERMITTED BY LAW, SCOUTLYTICS\'S TOTAL LIABILITY FOR ANY CLAIM ARISING OUT OF OR RELATING TO THE SERVICE OR THESE TERMS WILL NOT EXCEED THE GREATER OF (A) THE AMOUNT YOU PAID US FOR THE SERVICE IN THE TWELVE MONTHS BEFORE THE EVENT GIVING RISE TO THE CLAIM OR (B) ONE HUNDRED U.S. DOLLARS (US$100).',
    ],
  },
  {
    id: 'changes',
    title: '17. Changes to These Terms',
    paragraphs: [
      'We may update these Terms from time to time. When we do, we will revise the "Last updated" date above and, where appropriate, provide additional notice. Continued use of the Service after changes become effective constitutes acceptance of the updated Terms.',
    ],
  },
  {
    id: 'contact',
    title: '18. Contact',
    paragraphs: [
      `Questions about these Terms may be sent to ${SUPPORT_EMAIL}.`,
    ],
  },
];
